import { LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import { i18n, I18nController, isSupportedLocale } from "../i18n/index.ts";
import {
  handleChannelConfigReload as handleChannelConfigReloadInternal,
  handleChannelConfigSave as handleChannelConfigSaveInternal,
  handleNostrProfileCancel as handleNostrProfileCancelInternal,
  handleNostrProfileEdit as handleNostrProfileEditInternal,
  handleNostrProfileFieldChange as handleNostrProfileFieldChangeInternal,
  handleNostrProfileImport as handleNostrProfileImportInternal,
  handleNostrProfileSave as handleNostrProfileSaveInternal,
  handleNostrProfileToggleAdvanced as handleNostrProfileToggleAdvancedInternal,
  handleWhatsAppLogout as handleWhatsAppLogoutInternal,
  handleWhatsAppStart as handleWhatsAppStartInternal,
  handleWhatsAppWait as handleWhatsAppWaitInternal,
} from "./app-channels.ts";
import {
  handleAbortChat as handleAbortChatInternal,
  handleSendChat as handleSendChatInternal,
  removeQueuedMessage as removeQueuedMessageInternal,
} from "./app-chat.ts";
import { DEFAULT_CRON_FORM, DEFAULT_LOG_LEVEL_FILTERS } from "./app-defaults.ts";
import type { EventLogEntry } from "./app-events.ts";
import { connectGateway as connectGatewayInternal } from "./app-gateway.ts";
import {
  handleConnected,
  handleDisconnected,
  handleFirstUpdated,
  handleUpdated,
} from "./app-lifecycle.ts";
import { renderApp } from "./app-render.ts";
import {
  exportLogs as exportLogsInternal,
  handleChatScroll as handleChatScrollInternal,
  handleLogsScroll as handleLogsScrollInternal,
  resetChatScroll as resetChatScrollInternal,
  scheduleChatScroll as scheduleChatScrollInternal,
} from "./app-scroll.ts";
import {
  applySettings as applySettingsInternal,
  loadCron as loadCronInternal,
  loadOverview as loadOverviewInternal,
  setTab as setTabInternal,
  setTheme as setThemeInternal,
  onPopState as onPopStateInternal,
} from "./app-settings.ts";
import {
  resetToolStream as resetToolStreamInternal,
  type ToolStreamEntry,
  type CompactionStatus,
} from "./app-tool-stream.ts";
import type { AppViewState } from "./app-view-state.ts";
import { normalizeAssistantIdentity } from "./assistant-identity.ts";
import { loadAssistantIdentity as loadAssistantIdentityInternal } from "./controllers/assistant-identity.ts";
import {
  login as loginAuth,
  register as registerAuth,
  logout as logoutAuth,
} from "./controllers/auth.ts";
import type { DevicePairingList } from "./controllers/devices.ts";
import type { ExecApprovalRequest } from "./controllers/exec-approval.ts";
import type { ExecApprovalsFile, ExecApprovalsSnapshot } from "./controllers/exec-approvals.ts";
import type { SkillMessage } from "./controllers/skills.ts";
import { clearAllDeviceAuthTokens } from "./device-auth.ts";
import type { GatewayBrowserClient, GatewayHelloOk } from "./gateway.ts";
import type { Tab } from "./navigation.ts";
import { loadSettings, type UiSettings } from "./storage.ts";
import type { ResolvedTheme, ThemeMode } from "./theme.ts";
import type {
  AgentsListResult,
  AgentsFilesListResult,
  AgentIdentityResult,
  ConfigSnapshot,
  ConfigUiHints,
  CronJob,
  CronRunLogEntry,
  CronStatus,
  HealthSnapshot,
  LogEntry,
  LogLevel,
  PresenceEntry,
  ChannelsStatusSnapshot,
  SessionsListResult,
  SkillStatusReport,
  StatusSummary,
  NostrProfile,
} from "./types.ts";
import { type ChatAttachment, type ChatQueueItem, type CronFormState } from "./ui-types.ts";
import type { NostrProfileFormState } from "./views/channels.nostr-profile-form.ts";

declare global {
  interface Window {
    __TDAI_CONTROL_UI_BASE_PATH__?: string;
  }
}

const bootAssistantIdentity = normalizeAssistantIdentity({});

function resolveOnboardingMode(): boolean {
  if (!window.location.search) {
    return false;
  }
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("onboarding");
  if (!raw) {
    return false;
  }
  const normalized = raw.trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" || normalized === "on";
}

// Global flag to prevent duplicate Google OAuth callbacks (COOP issue causes multiple callbacks)
let googleAuthInProgress = false;
let googleAuthProcessed = false;

/** Reset Google auth flags (call on logout or when starting new auth flow) */
export function resetGoogleAuthFlags() {
  googleAuthInProgress = false;
  googleAuthProcessed = false;
}

@customElement("tdai-app")
export class OpenClawApp extends LitElement {
  private i18nController = new I18nController(this);
  @state() settings: UiSettings = loadSettings();
  constructor() {
    super();
    if (isSupportedLocale(this.settings.locale)) {
      void i18n.setLocale(this.settings.locale);
    }
  }
  @state() password = "";
  @state() tab: Tab = "chat";
  @state() onboarding = resolveOnboardingMode();
  @state() connected = false;
  @state() theme: ThemeMode = this.settings.theme ?? "system";
  @state() themeResolved: ResolvedTheme = "dark";
  @state() hello: GatewayHelloOk | null = null;
  @state() lastError: string | null = null;
  @state() eventLog: EventLogEntry[] = [];

  // Auth state for multi-user mode
  @state() authMode: "login" | "register" = "login";
  @state() authEmail = "";
  @state() authPassword = "";
  @state() authError: string | null = null;
  @state() authLoading = false;
  @state() showAuthLogin = false;
  private eventLogBuffer: EventLogEntry[] = [];
  private toolStreamSyncTimer: number | null = null;
  private sidebarCloseTimer: number | null = null;

  @state() assistantName = bootAssistantIdentity.name;
  @state() assistantAvatar = bootAssistantIdentity.avatar;
  @state() assistantAgentId = bootAssistantIdentity.agentId ?? null;

  @state() sessionKey = this.settings.sessionKey;
  @state() chatLoading = false;
  @state() chatSending = false;
  @state() chatMessage = "";
  @state() chatMessages: unknown[] = [];
  @state() chatToolMessages: unknown[] = [];
  @state() chatStream: string | null = null;
  @state() chatStreamStartedAt: number | null = null;
  @state() chatRunId: string | null = null;
  @state() compactionStatus: CompactionStatus | null = null;
  @state() chatAvatarUrl: string | null = null;
  @state() chatThinkingLevel: string | null = null;
  @state() chatQueue: ChatQueueItem[] = [];
  @state() chatAttachments: ChatAttachment[] = [];
  @state() chatManualRefreshInFlight = false;
  // Sidebar state for tool output viewing
  @state() sidebarOpen = false;
  @state() sidebarContent: string | null = null;
  @state() sidebarError: string | null = null;
  @state() splitRatio = this.settings.splitRatio;

  @state() nodesLoading = false;
  @state() nodes: Array<Record<string, unknown>> = [];
  @state() devicesLoading = false;
  @state() devicesError: string | null = null;
  @state() devicesList: DevicePairingList | null = null;
  @state() execApprovalsLoading = false;
  @state() execApprovalsSaving = false;
  @state() execApprovalsDirty = false;
  @state() execApprovalsSnapshot: ExecApprovalsSnapshot | null = null;
  @state() execApprovalsForm: ExecApprovalsFile | null = null;
  @state() execApprovalsSelectedAgent: string | null = null;
  @state() execApprovalsTarget: "gateway" | "node" = "gateway";
  @state() execApprovalsTargetNodeId: string | null = null;
  @state() execApprovalQueue: ExecApprovalRequest[] = [];
  @state() execApprovalBusy = false;
  @state() execApprovalError: string | null = null;
  @state() pendingGatewayUrl: string | null = null;

  @state() configLoading = false;
  @state() configRaw = "{\n}\n";
  @state() configRawOriginal = "";
  @state() configValid: boolean | null = null;
  @state() configIssues: unknown[] = [];
  @state() configSaving = false;
  @state() configApplying = false;
  @state() updateRunning = false;
  @state() applySessionKey = this.settings.lastActiveSessionKey;
  @state() configSnapshot: ConfigSnapshot | null = null;
  @state() configSchema: unknown = null;
  @state() configSchemaVersion: string | null = null;
  @state() configSchemaLoading = false;
  @state() configUiHints: ConfigUiHints = {};
  @state() configForm: Record<string, unknown> | null = null;
  @state() configFormOriginal: Record<string, unknown> | null = null;
  @state() configFormDirty = false;
  @state() configFormMode: "form" | "raw" = "form";
  @state() configSearchQuery = "";
  @state() configActiveSection: string | null = null;
  @state() configActiveSubsection: string | null = null;

  @state() channelsLoading = false;
  @state() channelsSnapshot: ChannelsStatusSnapshot | null = null;
  @state() channelsError: string | null = null;
  @state() channelsLastSuccess: number | null = null;
  @state() whatsappLoginMessage: string | null = null;
  @state() whatsappLoginQrDataUrl: string | null = null;
  @state() whatsappLoginConnected: boolean | null = null;
  @state() whatsappBusy = false;
  @state() nostrProfileFormState: NostrProfileFormState | null = null;
  @state() nostrProfileAccountId: string | null = null;

  @state() presenceLoading = false;
  @state() presenceEntries: PresenceEntry[] = [];
  @state() presenceError: string | null = null;
  @state() presenceStatus: string | null = null;

  @state() agentsLoading = false;
  @state() agentsList: AgentsListResult | null = null;
  @state() agentsError: string | null = null;
  @state() agentsSelectedId: string | null = null;
  @state() agentsPanel: "overview" | "files" | "tools" | "skills" | "channels" | "cron" =
    "overview";
  @state() agentFilesLoading = false;
  @state() agentFilesError: string | null = null;
  @state() agentFilesList: AgentsFilesListResult | null = null;
  @state() agentFileContents: Record<string, string> = {};
  @state() agentFileDrafts: Record<string, string> = {};
  @state() agentFileActive: string | null = null;
  @state() agentFileSaving = false;
  @state() agentIdentityLoading = false;
  @state() agentIdentityError: string | null = null;
  @state() agentIdentityById: Record<string, AgentIdentityResult> = {};
  @state() agentSkillsLoading = false;
  @state() agentSkillsError: string | null = null;
  @state() agentSkillsReport: SkillStatusReport | null = null;
  @state() agentSkillsAgentId: string | null = null;

  @state() sessionsLoading = false;
  @state() sessionsResult: SessionsListResult | null = null;
  @state() sessionsError: string | null = null;
  @state() sessionsFilterActive = "";
  @state() sessionsFilterLimit = "120";
  @state() sessionsIncludeGlobal = true;
  @state() sessionsIncludeUnknown = false;

  @state() usageLoading = false;
  @state() usageResult: import("./types.js").SessionsUsageResult | null = null;
  @state() usageCostSummary: import("./types.js").CostUsageSummary | null = null;
  @state() usageError: string | null = null;
  @state() usageStartDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  @state() usageEndDate = (() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  @state() usageSelectedSessions: string[] = [];
  @state() usageSelectedDays: string[] = [];
  @state() usageSelectedHours: number[] = [];
  @state() usageChartMode: "tokens" | "cost" = "tokens";
  @state() usageDailyChartMode: "total" | "by-type" = "by-type";
  @state() usageTimeSeriesMode: "cumulative" | "per-turn" = "per-turn";
  @state() usageTimeSeriesBreakdownMode: "total" | "by-type" = "by-type";
  @state() usageTimeSeries: import("./types.js").SessionUsageTimeSeries | null = null;
  @state() usageTimeSeriesLoading = false;
  @state() usageTimeSeriesCursorStart: number | null = null;
  @state() usageTimeSeriesCursorEnd: number | null = null;
  @state() usageSessionLogs: import("./views/usage.js").SessionLogEntry[] | null = null;
  @state() usageSessionLogsLoading = false;
  @state() usageSessionLogsExpanded = false;
  // Applied query (used to filter the already-loaded sessions list client-side).
  @state() usageQuery = "";
  // Draft query text (updates immediately as the user types; applied via debounce or "Search").
  @state() usageQueryDraft = "";
  @state() usageSessionSort: "tokens" | "cost" | "recent" | "messages" | "errors" = "recent";
  @state() usageSessionSortDir: "desc" | "asc" = "desc";
  @state() usageRecentSessions: string[] = [];
  @state() usageTimeZone: "local" | "utc" = "local";
  @state() usageContextExpanded = false;
  @state() usageHeaderPinned = false;
  @state() usageSessionsTab: "all" | "recent" = "all";
  @state() usageVisibleColumns: string[] = [
    "channel",
    "agent",
    "provider",
    "model",
    "messages",
    "tools",
    "errors",
    "duration",
  ];
  @state() usageLogFilterRoles: import("./views/usage.js").SessionLogRole[] = [];
  @state() usageLogFilterTools: string[] = [];
  @state() usageLogFilterHasTools = false;
  @state() usageLogFilterQuery = "";

  // Non-reactive (don't trigger renders just for timer bookkeeping).
  usageQueryDebounceTimer: number | null = null;

  @state() cronLoading = false;
  @state() cronJobs: CronJob[] = [];
  @state() cronStatus: CronStatus | null = null;
  @state() cronError: string | null = null;
  @state() cronForm: CronFormState = { ...DEFAULT_CRON_FORM };
  @state() cronRunsJobId: string | null = null;
  @state() cronRuns: CronRunLogEntry[] = [];
  @state() cronBusy = false;

  @state() skillsLoading = false;
  @state() skillsReport: SkillStatusReport | null = null;
  @state() skillsError: string | null = null;
  @state() skillsFilter = "";
  @state() skillEdits: Record<string, string> = {};
  @state() skillsBusyKey: string | null = null;
  @state() skillMessages: Record<string, SkillMessage> = {};

  @state() debugLoading = false;
  @state() debugStatus: StatusSummary | null = null;
  @state() debugHealth: HealthSnapshot | null = null;
  @state() debugModels: unknown[] = [];
  @state() debugHeartbeat: unknown = null;
  @state() debugCallMethod = "";
  @state() debugCallParams = "{}";
  @state() debugCallResult: string | null = null;
  @state() debugCallError: string | null = null;

  @state() logsLoading = false;
  @state() logsError: string | null = null;
  @state() logsFile: string | null = null;
  @state() logsEntries: LogEntry[] = [];
  @state() logsFilterText = "";
  @state() logsLevelFilters: Record<LogLevel, boolean> = {
    ...DEFAULT_LOG_LEVEL_FILTERS,
  };
  @state() logsAutoFollow = true;
  @state() logsTruncated = false;
  @state() logsCursor: number | null = null;

  // Browser panel state
  @state() browserPanelVisible = false;
  @state() browserPanelConnected = false;
  @state() browserPanelFullscreen = false;
  @state() browserNoVncUrl: string | null = null;
  @state() browserLiveViewUrl: string | null = null;
  @state() browserMode: "screenshot" | "novnc" | "liveview" = "screenshot";
  @state() browserScreenshot: string | null = null;
  @state() browserCurrentUrl: string | null = null;
  @state() browserLoading = false;
  @state() browserError: string | null = null;
  private browserPollingInterval: number | null = null;
  private browserAutoManaged = false;
  private navCollapsedBeforeBrowser = false;
  private sidebarOpenBeforeBrowser = false;
  @state() logsLastFetchAt: number | null = null;
  @state() logsLimit = 500;
  @state() logsMaxBytes = 250_000;
  @state() logsAtBottom = true;

  client: GatewayBrowserClient | null = null;
  private chatScrollFrame: number | null = null;
  private chatScrollTimeout: number | null = null;
  private chatHasAutoScrolled = false;
  private chatUserNearBottom = true;
  @state() chatNewMessagesBelow = false;
  private nodesPollInterval: number | null = null;
  private logsPollInterval: number | null = null;
  private debugPollInterval: number | null = null;
  private logsScrollFrame: number | null = null;
  private toolStreamById = new Map<string, ToolStreamEntry>();
  private toolStreamOrder: string[] = [];
  refreshSessionsAfterChat = new Set<string>();
  basePath = "";
  private popStateHandler = () =>
    onPopStateInternal(this as unknown as Parameters<typeof onPopStateInternal>[0]);
  private themeMedia: MediaQueryList | null = null;
  private themeMediaHandler: ((event: MediaQueryListEvent) => void) | null = null;
  private topbarObserver: ResizeObserver | null = null;

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    handleConnected(this as unknown as Parameters<typeof handleConnected>[0]);
  }

  protected firstUpdated() {
    handleFirstUpdated(this as unknown as Parameters<typeof handleFirstUpdated>[0]);
  }

  disconnectedCallback() {
    handleDisconnected(this as unknown as Parameters<typeof handleDisconnected>[0]);
    this.stopBrowserPolling();
    super.disconnectedCallback();
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    handleUpdated(this as unknown as Parameters<typeof handleUpdated>[0], changed);
    if (changed.has("browserPanelVisible")) {
      if (this.browserPanelVisible) {
        this.startBrowserPolling();
      } else {
        this.stopBrowserPolling();
      }
    }
  }

  connect() {
    connectGatewayInternal(this as unknown as Parameters<typeof connectGatewayInternal>[0]);
  }

  // Auth methods for multi-user mode
  async handleAuthSubmit() {
    if (this.authLoading) {
      return;
    }

    this.authLoading = true;
    this.authError = null;

    try {
      const result =
        this.authMode === "login"
          ? await loginAuth(this, { email: this.authEmail, password: this.authPassword })
          : await registerAuth(this, { email: this.authEmail, password: this.authPassword });

      if (result.ok) {
        // Clear device auth tokens to ensure JWT is used instead
        clearAllDeviceAuthTokens();

        this.showAuthLogin = false;
        this.authEmail = "";
        this.authPassword = "";
        this.authError = null;
        // Reconnect with JWT
        this.connect();
      } else {
        this.authError = result.error || "Authentication failed";
      }
    } catch (err) {
      this.authError = err instanceof Error ? err.message : "Authentication failed";
    } finally {
      this.authLoading = false;
    }
  }

  handleAuthEmailChange(email: string) {
    this.authEmail = email;
  }

  handleAuthPasswordChange(password: string) {
    this.authPassword = password;
  }

  handleAuthToggleMode() {
    this.authMode = this.authMode === "login" ? "register" : "login";
    this.authError = null;
  }

  handleAuthGoogleLogin() {
    // Prevent multiple simultaneous auth attempts
    if (googleAuthInProgress) {
      console.log("[TDAI Auth] Google auth already in progress, ignoring");
      return;
    }

    // Google OAuth using Google Identity Services (popup flow)
    const clientId = "913930659399-kqv95838c6oqlvjmvvh6lr0k16367l2o.apps.googleusercontent.com";

    // Check if google oauth2 is loaded
    const google = (
      window as unknown as {
        google?: {
          accounts?: {
            oauth2?: {
              initTokenClient: (config: {
                client_id: string;
                scope: string;
                callback: (response: { access_token?: string; error?: string }) => void;
                error_callback?: (error: { type: string; message?: string }) => void;
              }) => { requestAccessToken: () => void };
            };
          };
        };
      }
    ).google;

    if (!google?.accounts?.oauth2) {
      this.authError = "Google Sign-In is loading, please try again";
      return;
    }

    // Set global flags
    googleAuthInProgress = true;
    googleAuthProcessed = false;

    this.authLoading = true;
    this.authError = null;

    // Use OAuth2 popup flow (more reliable than One Tap)
    const client = google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: "email profile openid",
      callback: async (tokenResponse) => {
        // Prevent duplicate callback calls (COOP issue can cause multiple callbacks)
        if (googleAuthProcessed) {
          console.log("[TDAI Auth] Ignoring duplicate callback (global flag)");
          return;
        }
        googleAuthProcessed = true;

        if (tokenResponse.error || !tokenResponse.access_token) {
          googleAuthInProgress = false;
          this.authLoading = false;
          this.authError = tokenResponse.error || "Google Sign-In failed";
          return;
        }

        try {
          console.log("[TDAI Auth] Processing Google token...");

          // Get user info from Google API using access token
          const userInfoResponse = await fetch("https://www.googleapis.com/oauth2/v3/userinfo", {
            headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
          });
          const userInfo = await userInfoResponse.json();

          // Call backend Google auth endpoint
          const baseUrl = this.settings.gatewayUrl.replace(/^wss?:/, (m) =>
            m === "wss:" ? "https:" : "http:",
          );
          const authResponse = await fetch(`${baseUrl.replace(/\/$/, "")}/auth/google`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              googleId: userInfo.sub,
              email: userInfo.email,
              name: userInfo.name,
              avatarUrl: userInfo.picture,
              emailVerified: userInfo.email_verified,
            }),
          });

          const data = await authResponse.json();
          console.log("[TDAI Auth] /auth/google response:", data);

          if (data.ok && data.accessToken) {
            // Clear device auth tokens to ensure JWT is used instead
            // Device tokens from previous sessions would override the JWT
            clearAllDeviceAuthTokens();

            // Store token and connect
            console.log("[TDAI Auth] Storing token and connecting...");
            localStorage.setItem("tdai_access_token", data.accessToken);
            if (data.refreshToken) {
              localStorage.setItem("tdai_refresh_token", data.refreshToken);
            }
            if (data.user) {
              localStorage.setItem("tdai_user", JSON.stringify(data.user));
            }

            // Clear any previous errors and hide login form
            this.lastError = null;
            this.showAuthLogin = false;
            this.authLoading = false;
            googleAuthInProgress = false;
            console.log("[TDAI Auth] Auth successful, calling connect()...");
            this.connect();
          } else {
            console.log("[TDAI Auth] Auth failed:", data.error);
            googleAuthInProgress = false;
            this.authLoading = false;
            this.authError = data.error || "Authentication failed";
          }
        } catch (err) {
          console.error("[TDAI Auth] Error:", err);
          googleAuthInProgress = false;
          this.authLoading = false;
          this.authError = err instanceof Error ? err.message : "Authentication failed";
        }
      },
      error_callback: (error) => {
        if (googleAuthProcessed) {
          return;
        }
        googleAuthInProgress = false;
        this.authLoading = false;
        if (error.type === "popup_closed") {
          // User closed popup - not an error, just reset loading state
          return;
        }
        this.authError = error.message || `Google Sign-In error: ${error.type}`;
      },
    });

    // Open Google Sign-In popup
    client.requestAccessToken();
  }

  handleAuthForgotPassword() {
    this.authError = "Password reset coming soon";
  }

  async handleLogout() {
    await logoutAuth(this);
    resetGoogleAuthFlags();
    this.showAuthLogin = true;
    this.connected = false;
  }

  handleChatScroll(event: Event) {
    handleChatScrollInternal(
      this as unknown as Parameters<typeof handleChatScrollInternal>[0],
      event,
    );
  }

  handleLogsScroll(event: Event) {
    handleLogsScrollInternal(
      this as unknown as Parameters<typeof handleLogsScrollInternal>[0],
      event,
    );
  }

  exportLogs(lines: string[], label: string) {
    exportLogsInternal(lines, label);
  }

  resetToolStream() {
    resetToolStreamInternal(this as unknown as Parameters<typeof resetToolStreamInternal>[0]);
  }

  resetChatScroll() {
    resetChatScrollInternal(this as unknown as Parameters<typeof resetChatScrollInternal>[0]);
  }

  scrollToBottom(opts?: { smooth?: boolean }) {
    resetChatScrollInternal(this as unknown as Parameters<typeof resetChatScrollInternal>[0]);
    scheduleChatScrollInternal(
      this as unknown as Parameters<typeof scheduleChatScrollInternal>[0],
      true,
      Boolean(opts?.smooth),
    );
  }

  async loadAssistantIdentity() {
    await loadAssistantIdentityInternal(this);
  }

  applySettings(next: UiSettings) {
    applySettingsInternal(this as unknown as Parameters<typeof applySettingsInternal>[0], next);
  }

  setTab(next: Tab) {
    setTabInternal(this as unknown as Parameters<typeof setTabInternal>[0], next);
  }

  setTheme(next: ThemeMode, context?: Parameters<typeof setThemeInternal>[2]) {
    setThemeInternal(this as unknown as Parameters<typeof setThemeInternal>[0], next, context);
  }

  async loadOverview() {
    await loadOverviewInternal(this as unknown as Parameters<typeof loadOverviewInternal>[0]);
  }

  async loadCron() {
    await loadCronInternal(this as unknown as Parameters<typeof loadCronInternal>[0]);
  }

  async handleAbortChat() {
    await handleAbortChatInternal(this as unknown as Parameters<typeof handleAbortChatInternal>[0]);
  }

  removeQueuedMessage(id: string) {
    removeQueuedMessageInternal(
      this as unknown as Parameters<typeof removeQueuedMessageInternal>[0],
      id,
    );
  }

  async handleSendChat(
    messageOverride?: string,
    opts?: Parameters<typeof handleSendChatInternal>[2],
  ) {
    await handleSendChatInternal(
      this as unknown as Parameters<typeof handleSendChatInternal>[0],
      messageOverride,
      opts,
    );
  }

  async handleWhatsAppStart(force: boolean) {
    await handleWhatsAppStartInternal(this, force);
  }

  async handleWhatsAppWait() {
    await handleWhatsAppWaitInternal(this);
  }

  async handleWhatsAppLogout() {
    await handleWhatsAppLogoutInternal(this);
  }

  async handleChannelConfigSave() {
    await handleChannelConfigSaveInternal(this);
  }

  async handleChannelConfigReload() {
    await handleChannelConfigReloadInternal(this);
  }

  handleNostrProfileEdit(accountId: string, profile: NostrProfile | null) {
    handleNostrProfileEditInternal(this, accountId, profile);
  }

  handleNostrProfileCancel() {
    handleNostrProfileCancelInternal(this);
  }

  handleNostrProfileFieldChange(field: keyof NostrProfile, value: string) {
    handleNostrProfileFieldChangeInternal(this, field, value);
  }

  async handleNostrProfileSave() {
    await handleNostrProfileSaveInternal(this);
  }

  async handleNostrProfileImport() {
    await handleNostrProfileImportInternal(this);
  }

  handleNostrProfileToggleAdvanced() {
    handleNostrProfileToggleAdvancedInternal(this);
  }

  async handleExecApprovalDecision(decision: "allow-once" | "allow-always" | "deny") {
    const active = this.execApprovalQueue[0];
    if (!active || !this.client || this.execApprovalBusy) {
      return;
    }
    this.execApprovalBusy = true;
    this.execApprovalError = null;
    try {
      await this.client.request("exec.approval.resolve", {
        id: active.id,
        decision,
      });
      this.execApprovalQueue = this.execApprovalQueue.filter((entry) => entry.id !== active.id);
    } catch (err) {
      this.execApprovalError = `Exec approval failed: ${String(err)}`;
    } finally {
      this.execApprovalBusy = false;
    }
  }

  handleGatewayUrlConfirm() {
    const nextGatewayUrl = this.pendingGatewayUrl;
    if (!nextGatewayUrl) {
      return;
    }
    this.pendingGatewayUrl = null;
    applySettingsInternal(this as unknown as Parameters<typeof applySettingsInternal>[0], {
      ...this.settings,
      gatewayUrl: nextGatewayUrl,
    });
    this.connect();
  }

  handleGatewayUrlCancel() {
    this.pendingGatewayUrl = null;
  }

  // Sidebar handlers for tool output viewing
  handleOpenSidebar(content: string) {
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
      this.sidebarCloseTimer = null;
    }
    this.sidebarContent = content;
    this.sidebarError = null;
    this.sidebarOpen = true;
  }

  handleCloseSidebar() {
    this.sidebarOpen = false;
    // Clear content after transition
    if (this.sidebarCloseTimer != null) {
      window.clearTimeout(this.sidebarCloseTimer);
    }
    this.sidebarCloseTimer = window.setTimeout(() => {
      if (this.sidebarOpen) {
        return;
      }
      this.sidebarContent = null;
      this.sidebarError = null;
      this.sidebarCloseTimer = null;
    }, 200);
  }

  handleSplitRatioChange(ratio: number) {
    const newRatio = Math.max(0.4, Math.min(0.7, ratio));
    this.splitRatio = newRatio;
    this.applySettings({ ...this.settings, splitRatio: newRatio });
  }

  private openBrowserPanel(autoManaged = false) {
    if (this.browserPanelVisible) {
      if (!autoManaged) {
        this.browserAutoManaged = false;
      }
      return;
    }

    this.browserPanelVisible = true;
    this.browserAutoManaged = autoManaged;

    // Remember current nav state and collapse.
    this.navCollapsedBeforeBrowser = this.settings.navCollapsed;
    if (!this.settings.navCollapsed) {
      this.applySettings({ ...this.settings, navCollapsed: true });
    }

    // Remember and close tool output sidebar (it gets squeezed and less useful with split view).
    this.sidebarOpenBeforeBrowser = this.sidebarOpen;
    if (this.sidebarOpen) {
      this.sidebarOpen = false;
    }

    void this.handleBrowserStatus();
    if (this.browserLiveViewUrl) {
      this.browserMode = "liveview";
    }
  }

  private closeBrowserPanel() {
    if (!this.browserPanelVisible) {
      this.browserAutoManaged = false;
      return;
    }

    this.browserPanelVisible = false;
    this.browserAutoManaged = false;

    // Restore nav state when closing browser.
    if (this.settings.navCollapsed !== this.navCollapsedBeforeBrowser) {
      this.applySettings({ ...this.settings, navCollapsed: this.navCollapsedBeforeBrowser });
    }

    // Restore tool output sidebar if it was open before browser split view.
    if (this.sidebarOpenBeforeBrowser && !this.sidebarOpen) {
      this.sidebarOpen = true;
    }
  }

  handleBrowserAutoClose() {
    if (!this.browserPanelVisible || !this.browserAutoManaged) {
      return;
    }
    this.closeBrowserPanel();
  }

  // Browser panel handlers
  handleBrowserToggle() {
    if (this.browserPanelVisible) {
      this.closeBrowserPanel();
      return;
    }
    this.openBrowserPanel(false);
  }

  async handleBrowserStatus() {
    console.log("[browser] handleBrowserStatus called", { client: !!this.client });
    if (!this.client) {
      console.log("[browser] handleBrowserStatus aborted - no client");
      return;
    }

    try {
      console.log("[browser] About to call browser.request for status");
      const result = await this.client.request<{
        enabled?: boolean;
        profile?: string;
        running?: boolean;
        liveViewUrl?: string;
      }>("browser.request", {
        method: "GET",
        path: "/",
      });
      console.log("[browser] browser.request status completed", result);

      // Update live view URL if provided (Kernel session)
      if (result.liveViewUrl && result.liveViewUrl !== this.browserLiveViewUrl) {
        console.log("[browser] Kernel live view URL received:", result.liveViewUrl);
        this.browserLiveViewUrl = result.liveViewUrl;
        this.browserMode = "liveview";
        console.log("[browser] Switched to liveview mode");
      }
    } catch (err) {
      console.error("[browser] browser.request status failed:", err);
      // Non-critical error - continue with screenshot polling
    }
  }

  /**
   * Auto-open Browser Panel when message contains browser keywords.
   * Called before sending message to agent.
   */
  autoBrowserOpen(message: string) {
    if (!message || this.browserPanelVisible) {
      return;
    }

    const keywords = [
      "browser",
      "chrome",
    ];
    const lowercaseMsg = message.toLowerCase();

    if (keywords.some((kw) => lowercaseMsg.includes(kw))) {
      console.log("[browser] Auto-opening Browser Panel - keyword detected in message");
      this.openBrowserPanel(true);
    }
  }

  handleBrowserAutoOpenFromTool(toolName?: string) {
    if (this.browserPanelVisible) {
      return;
    }
    const normalized = (toolName ?? "").toLowerCase();
    const isBrowserTool =
      normalized.includes("browser") ||
      normalized.includes("playwright") ||
      normalized.includes("web");
    if (!isBrowserTool) {
      return;
    }
    this.openBrowserPanel(true);
  }

  handleBrowserFullscreenToggle() {
    this.browserPanelFullscreen = !this.browserPanelFullscreen;
  }

  handleBrowserConnectionChange(connected: boolean) {
    this.browserPanelConnected = connected;
  }

  private startBrowserPolling() {
    if (this.browserPollingInterval) {
      return;
    }
    if (this.browserMode === "liveview") {
      return;
    }
    // Take initial screenshot immediately
    void this.handleBrowserScreenshot();
    // Then poll every 2 seconds
    this.browserPollingInterval = window.setInterval(() => {
      if (this.browserPanelVisible && !this.browserLoading && this.browserMode !== "liveview") {
        void this.handleBrowserScreenshot();
      }
    }, 2000);
  }

  private stopBrowserPolling() {
    if (this.browserPollingInterval) {
      window.clearInterval(this.browserPollingInterval);
      this.browserPollingInterval = null;
    }
  }

  // Auto-mode stub (called from gateway connection)
  startBrowserAutoMode() {
    // For now, just ensure panel can be toggled
    // Future: auto-show when browser has tabs
  }

  // Screenshot-based browser control (Kernel style)
  async handleBrowserScreenshot() {
    console.log("[browser] handleBrowserScreenshot called", {
      client: !!this.client,
      loading: this.browserLoading,
    });
    if (!this.client || this.browserLoading) {
      console.log("[browser] handleBrowserScreenshot aborted", {
        client: !!this.client,
        loading: this.browserLoading,
      });
      return;
    }

    // Auto-open browser panel on first browser action for real-time viewing
    if (!this.browserPanelVisible) {
      console.log("[browser] Auto-opening Browser Panel for real-time view");
      this.openBrowserPanel(true);
    }

    this.browserLoading = true;
    this.browserError = null;
    try {
      console.log("[browser] About to call browser.request", {
        clientExists: !!this.client,
        clientConnected: this.client?.connected,
      });
      const result = await this.client.request<{
        ok?: boolean;
        base64?: string;
        path?: string;
        url?: string;
        liveViewUrl?: string;
      }>("browser.request", {
        method: "POST",
        path: "/screenshot",
        body: { type: "png", includeData: true },
      });
      console.log("[browser] browser.request completed");
      console.log("[browser] screenshot result", {
        ok: result?.ok,
        hasBase64: !!result?.base64,
        url: result?.url,
        liveViewUrl: result?.liveViewUrl,
      });
      if (result?.ok && result.base64) {
        // Only update if screenshot changed (prevents flickering)
        if (this.browserScreenshot !== result.base64) {
          this.browserScreenshot = result.base64;
        }
        if (result.url && result.url !== this.browserCurrentUrl) {
          this.browserCurrentUrl = result.url;
        }
        // Update live view URL if provided (Kernel session)
        if (result.liveViewUrl && result.liveViewUrl !== this.browserLiveViewUrl) {
          console.log("[browser] Kernel live view URL received:", result.liveViewUrl);
          this.browserLiveViewUrl = result.liveViewUrl;
          this.browserMode = "liveview"; // Switch to live view mode
        }
        this.browserPanelConnected = true;
      } else if (result && !result.ok) {
        // API returned error
        this.browserError = (result as { error?: string }).error || "Screenshot failed";
        this.browserPanelConnected = false;
      } else if (!result?.base64) {
        // No screenshot data returned
        this.browserError = "No screenshot data received";
      }
    } catch (err) {
      console.error("[browser] browser.request failed:", err);
      this.browserError = err instanceof Error ? err.message : String(err);
    } finally {
      this.browserLoading = false;
      console.log("[browser] handleBrowserScreenshot finished");
    }
  }

  async handleBrowserNavigate(url: string) {
    if (!this.client || this.browserLoading) {
      return;
    }
    this.browserLoading = true;
    this.browserError = null;
    try {
      const result = await this.client.request("browser.request", {
        method: "POST",
        path: "/navigate",
        body: { url },
      });
      if ((result as { ok?: boolean }).ok) {
        this.browserCurrentUrl = url;
        await this.handleBrowserScreenshot();
      }
    } catch (err) {
      this.browserError = err instanceof Error ? err.message : String(err);
      this.browserLoading = false;
    }
  }

  async handleBrowserClick(x: number, y: number) {
    if (!this.client || this.browserLoading) {
      return;
    }
    this.browserLoading = true;
    this.browserError = null;
    try {
      await this.client.request("browser.request", {
        method: "POST",
        path: "/act",
        body: { kind: "click", x, y },
      });
      // Take screenshot after click
      await this.handleBrowserScreenshot();
    } catch (err) {
      this.browserError = err instanceof Error ? err.message : String(err);
      this.browserLoading = false;
    }
  }

  async handleBrowserType(text: string) {
    if (!this.client || this.browserLoading) {
      return;
    }
    this.browserLoading = true;
    this.browserError = null;
    try {
      await this.client.request("browser.request", {
        method: "POST",
        path: "/act",
        body: { kind: "type", text },
      });
      // Take screenshot after typing
      await this.handleBrowserScreenshot();
    } catch (err) {
      this.browserError = err instanceof Error ? err.message : String(err);
      this.browserLoading = false;
    }
  }

  async handleBrowserKey(key: string) {
    if (!this.client || this.browserLoading) {
      return;
    }
    this.browserLoading = true;
    this.browserError = null;
    try {
      await this.client.request("browser.request", {
        method: "POST",
        path: "/act",
        body: { kind: "press", key },
      });
      // Take screenshot after key press
      await this.handleBrowserScreenshot();
    } catch (err) {
      this.browserError = err instanceof Error ? err.message : String(err);
      this.browserLoading = false;
    }
  }

  render() {
    return renderApp(this as unknown as AppViewState);
  }
}
